import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Inbox, RefreshCw, CheckCircle2, XCircle, Loader2, Mail, Phone, Hash, Clock, AlertTriangle, PauseCircle, TimerReset } from "lucide-react";
import { API } from "../../config";
import { dealerApi } from "../../utils/dealerApi";
import { fmtI18n } from "../../translations/dealerChangeRequests";

// ── Field metadata: icons only; labels come from the i18n pack ──────
//
// Zorzutti CR 05/08/2026 — previously carried Italian ``label`` values
// which leaked into non-IT dealer portals (Poland reported the whole
// view rendered in Italian even though the UI language was PL). The
// labels are now looked up from the ``t.crFieldLabelEmail`` / Phone /
// TaxCode keys with an UK fallback via ``getDealerTranslation``.
const FIELD_META = {
  email:    { i18nKey: "crFieldLabelEmail",   icon: Mail },
  phone:    { i18nKey: "crFieldLabelPhone",   icon: Phone },
  tax_code: { i18nKey: "crFieldLabelTaxCode", icon: Hash },
};

// ── SLA policy (Zorzutti CR 03/08/2026) ─────────────────────────────
// The customer-facing "Procedura sospesa" page promises: "Ti scriveremo
// entro 24 ore". This guard makes that promise VISIBLE to the dealer:
//
//   • 0h..<12h   → "ok"     — plenty of time, no alarm
//   • 12h..<24h → "warn"   — amber: needs attention before the window closes
//   • ≥24h      → "breach" — red: the 24h SLA promised to the customer HAS been broken
//
// Only pre-signature requests are SLA-tracked (post-signature edits are
// less time-sensitive and don't block the customer's activation flow).
const SLA_WARN_H = 12;
const SLA_BREACH_H = 24;

function slaStatus(iso) {
  if (!iso) return { level: "ok", hours: 0 };
  try {
    const hours = (Date.now() - new Date(iso).getTime()) / 3600000;
    if (hours >= SLA_BREACH_H) return { level: "breach", hours };
    if (hours >= SLA_WARN_H) return { level: "warn", hours };
    return { level: "ok", hours };
  } catch (_) {
    return { level: "ok", hours: 0 };
  }
}

// ``locale`` comes from ``t.crDateTimeLocale`` (BCP-47), falling back
// to en-GB when the dealer's language pack has none (safer than IT).
function fmtDateTime(iso, locale) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(locale || "en-GB", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch (_) { return iso.slice(0, 16); }
}

export default function DealerChangeRequestsView({
  dealerToken,
  onCountChange,
  initialRequestId = null,        // deep-link target (Zorzutti CR 31/07/2026)
  onDeepLinkConsumed = () => {},  // parent clears the pending state
  t = {},                          // i18n bundle from getDealerTranslation
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [rejectionInputs, setRejectionInputs] = useState({});
  // Card currently being highlighted via deep-link (mail CTA). Cleared
  // after 3s so the visual accent doesn't linger and become noise.
  const [highlightedId, setHighlightedId] = useState(null);
  // Refs per card so we can scroll into view once the fetch resolves.
  const cardRefs = useRef({});

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const res = await dealerApi.get(`/dealer/customer-update-requests`, { token: dealerToken });
      const list = res.data.items || [];
      setItems(list);
      if (onCountChange) onCountChange(list.length);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t.crLoadError || "Error loading requests");
    } finally {
      setLoading(false);
    }
  }, [dealerToken, onCountChange, t.crLoadError]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  // Deep-link consumer: once the fetch resolves, scroll to the target
  // card and pulse-highlight it for ~3s. Guarded by a ref that stores
  // the LAST processed request id (not a plain boolean) so that:
  //   • the effect is IDEMPOTENT within a single deep-link (the parent
  //     clears its own state on consume, which propagates back as a
  //     new render — we early-return instead of re-scheduling timers);
  //   • a NEW deep-link with a DIFFERENT id (e.g. dealer already on
  //     this view + arrives via a second email link) DOES re-fire
  //     (was blocked by the old boolean ref — code review LOW #3
  //     01/08/2026).
  // All setTimeout handles are stored and cleared on unmount so the
  // component doesn't call setState after being torn down (code review
  // LOW #2 01/08/2026).
  const consumedRef = useRef(null);
  useEffect(() => {
    if (loading || !initialRequestId || consumedRef.current === initialRequestId) return;
    consumedRef.current = initialRequestId;
    const found = items.find((r) => r.id === initialRequestId);
    const timers = [];
    if (found) {
      // Wait one paint so the DOM node is mounted, then scroll+glow.
      timers.push(setTimeout(() => {
        const el = cardRefs.current[initialRequestId];
        if (el && el.scrollIntoView) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
        setHighlightedId(initialRequestId);
      }, 50));
      timers.push(setTimeout(() => setHighlightedId(null), 3200));
      // Notify the parent AFTER the pulse window so the URL-scrubbing
      // re-render doesn't clip the animation short.
      timers.push(setTimeout(() => onDeepLinkConsumed(), 3300));
      return () => { timers.forEach(clearTimeout); };
    }
    // Request is no longer in the pending list — likely already handled.
    toast.info(t.crDeepLinkGone || "The request linked from the email is no longer pending: it may have already been approved or rejected.");
    onDeepLinkConsumed();
  }, [loading, initialRequestId, items, onDeepLinkConsumed, t.crDeepLinkGone]);

  // Field label resolver — falls back to raw field name when the pack
  // has no explicit entry for a new field type (e.g. future ``iban``).
  const fieldLabel = (fieldKey) => {
    const meta = FIELD_META[fieldKey];
    if (!meta) return fieldKey;
    return t[meta.i18nKey] || fieldKey;
  };

  const handleApprove = async (req) => {
    const confirmMsg = fmtI18n(t.crConfirmApply, {
      value: req.new_value,
      field: fieldLabel(req.field),
      name: req.customer_name,
    }) || `Apply "${req.new_value}" to ${fieldLabel(req.field)} for customer ${req.customer_name}?`;
    if (!window.confirm(confirmMsg)) return;
    setBusyId(req.id);
    try {
      await dealerApi.post(
        `/dealer/customer-update-requests/${req.id}/approve`,
        { token: dealerToken },
      );
      toast.success(t.crApprovedToast || "Request approved: customer data updated");
      setItems((prev) => {
        const next = prev.filter((r) => r.id !== req.id);
        if (onCountChange) onCountChange(next.length);
        return next;
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || t.crApproveError || "Error approving");
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async (req) => {
    const reason = rejectionInputs[req.id] || "";
    setBusyId(req.id);
    try {
      await dealerApi.post(
        `/dealer/customer-update-requests/${req.id}/reject`,
        { token: dealerToken },
        { reason },
      );
      toast.success(t.crRejectedToast || "Request rejected");
      setItems((prev) => {
        const next = prev.filter((r) => r.id !== req.id);
        if (onCountChange) onCountChange(next.length);
        return next;
      });
      setRejectionInputs((prev) => { const c = { ...prev }; delete c[req.id]; return c; });
    } catch (e) {
      toast.error(e?.response?.data?.detail || t.crRejectError || "Error rejecting");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="max-w-4xl" data-testid="dealer-change-requests-view">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Inbox size={22} className="text-lojack-red" />
            {t.crTitle || "Customer data change requests"}
          </h2>
          <p className="text-gray-500 text-sm mt-1">
            {t.crSubtitle || "Customers have requested changes to locked fields. Approve or reject."}
          </p>
        </div>
        <button
          onClick={fetchRequests}
          disabled={loading}
          className="p-2 hover:bg-gray-800 rounded-lg text-gray-400 hover:text-white transition"
          data-testid="dealer-change-requests-refresh"
        >
          <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 size={28} className="text-gray-500 animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="bg-[#111] border border-gray-800 rounded-xl p-12 text-center" data-testid="dealer-change-requests-empty">
          <Inbox size={40} className="text-gray-700 mx-auto mb-3" />
          <p className="text-gray-400 font-medium">{t.crEmpty || "No pending requests"}</p>
          <p className="text-gray-600 text-sm mt-1">{t.crEmptyHint || "Customer requests will appear here."}</p>
        </div>
      ) : (() => {
        // Fabrizio CR 31/07/2026: split by origin so pre-signature
        // requests (which BLOCK the customer's wallet procedure) are
        // shown first with URGENT styling. Post-signature (OTP-based)
        // requests are less time-sensitive.
        const preSigRaw = items.filter((r) => r.origin === "pre_signature_wallet");
        const postSig = items.filter((r) => r.origin !== "pre_signature_wallet");

        // Zorzutti CR 03/08/2026 — SLA-aware ordering + counters.
        // Sort so the request closest to (or past) the 24h line is
        // rendered first: dealer sees which ones to grab NOW to keep
        // the customer-facing promise ("Ti scriveremo entro 24 ore").
        const preSig = [...preSigRaw].sort(
          (a, b) => new Date(a.requested_at).getTime() - new Date(b.requested_at).getTime()
        );
        const slaCounts = preSig.reduce(
          (acc, r) => {
            acc[slaStatus(r.requested_at).level] += 1;
            return acc;
          },
          { ok: 0, warn: 0, breach: 0 }
        );

        const renderCard = (req, urgent) => {
          const meta = FIELD_META[req.field];
          const Icon = meta ? meta.icon : Hash;
          const label = fieldLabel(req.field);
          const isBusy = busyId === req.id;
          const isDeepLinked = highlightedId === req.id;
          return (
            <div
              key={req.id}
              ref={(el) => { cardRefs.current[req.id] = el; }}
              className={`rounded-xl p-5 transition-all duration-500 ${
                urgent
                  ? "bg-gradient-to-br from-red-950/40 via-[#111] to-[#111] border border-red-800/60 shadow-lg shadow-red-900/20"
                  : "bg-[#111] border border-gray-800"
              } ${isDeepLinked ? "ring-2 ring-lojack-red ring-offset-2 ring-offset-black animate-pulse-slow" : ""}`}
              data-testid={`change-request-${req.id}`}
              data-deeplinked={isDeepLinked ? "true" : undefined}
            >
              {isDeepLinked && (
                <div
                  className="mb-3 flex items-center gap-2 text-lojack-red text-xs font-semibold uppercase tracking-wider"
                  data-testid={`change-request-deeplink-banner-${req.id}`}
                >
                  <Mail size={13} />
                  {t.crDeepLinkBanner || "Opened directly from notification email"}
                </div>
              )}
              {urgent && (() => {
                const sla = slaStatus(req.requested_at);
                const wholeHours = Math.max(0, Math.floor(sla.hours));
                const remaining = Math.max(0, SLA_BREACH_H - wholeHours);
                if (sla.level === "breach") {
                  return (
                    <div
                      className="mb-4 inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-red-600/20 border border-red-500 text-red-100 text-xs font-bold uppercase tracking-wider animate-pulse"
                      data-testid={`change-request-sla-badge-${req.id}`}
                      data-sla-level="breach"
                    >
                      <AlertTriangle size={13} />
                      {fmtI18n(t.crSlaBadgeBreach, { h: wholeHours }) || `SLA breached · ${wholeHours}h since submission`}
                    </div>
                  );
                }
                if (sla.level === "warn") {
                  return (
                    <div
                      className="mb-4 inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-amber-600/20 border border-amber-500 text-amber-100 text-xs font-bold uppercase tracking-wider"
                      data-testid={`change-request-sla-badge-${req.id}`}
                      data-sla-level="warn"
                    >
                      <TimerReset size={13} />
                      {fmtI18n(t.crSlaBadgeWarn, { h: wholeHours, rem: remaining }) || `Warning · ${wholeHours}h · expires in ${remaining}h`}
                    </div>
                  );
                }
                return (
                  <div
                    className="mb-4 inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-emerald-900/30 border border-emerald-700/60 text-emerald-200 text-xs font-semibold uppercase tracking-wider"
                    data-testid={`change-request-sla-badge-${req.id}`}
                    data-sla-level="ok"
                  >
                    <Clock size={13} />
                    {fmtI18n(t.crSlaBadgeOk, { h: wholeHours, rem: remaining }) || `On time · ${wholeHours}h · ${remaining}h to deadline`}
                  </div>
                );
              })()}
              <div className="flex items-start gap-4 flex-wrap">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                  urgent
                    ? "bg-red-900/40 border border-red-700"
                    : "bg-lojack-red/10 border border-lojack-red/30"
                }`}>
                  <Icon size={18} className={urgent ? "text-red-300" : "text-lojack-red"} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-white text-base">{req.customer_name}</span>
                    <span className="text-gray-500 text-xs">·</span>
                    <span className="text-gray-400 text-sm truncate">{req.customer_email}</span>
                  </div>
                  <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-gray-600 font-semibold mb-1">{t.crFieldSection || "Field"}</div>
                      <div className="text-sm text-white font-medium">{label}</div>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <Clock size={12} />
                      {fmtDateTime(req.requested_at, t.crDateTimeLocale)}
                    </div>
                  </div>
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-gray-600 font-semibold mb-1">{t.crCurrentValue || "Current value"}</div>
                      <code className="block text-xs bg-[#0A0A0A] border border-gray-800 rounded px-2 py-1.5 text-gray-400 break-all">
                        {req.current_value || "—"}
                      </code>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-lojack-red font-semibold mb-1">{t.crNewValueRequested || "New requested value"}</div>
                      <code className="block text-xs bg-lojack-red/5 border border-lojack-red/30 rounded px-2 py-1.5 text-white font-medium break-all" data-testid={`change-request-new-${req.id}`}>
                        {req.new_value}
                      </code>
                    </div>
                  </div>
                  {req.note && (
                    <div className="mt-3">
                      <div className="text-[10px] uppercase tracking-wider text-gray-600 font-semibold mb-1">{t.crCustomerNote || "Customer's motivation"}</div>
                      <p className="text-xs text-gray-400 italic bg-[#0A0A0A] border border-gray-800 rounded px-3 py-2">
                        {req.note}
                      </p>
                    </div>
                  )}
                  {urgent && (() => {
                    const targetKey = req.field === "email" ? "crApproveWarningTargetEmail" : "crApproveWarningTargetCustomer";
                    // The warning template has one {field} and one {target}
                    // token; we substitute both here so the ORDER remains
                    // idiomatic in each language (unlike a static concat).
                    // ``field`` is wrapped in <strong> at render-time by
                    // splitting the interpolated result on the field label.
                    const warningRaw = fmtI18n(t.crApproveWarning, {
                      field: `\u0000FIELD\u0000`,
                      target: t[targetKey] || (req.field === "email" ? "new address" : "customer"),
                    }) || `Approving will update the customer's ${label} field and a new activation email will be sent AUTOMATICALLY. The old link will become unusable.`;
                    const parts = warningRaw.split(`\u0000FIELD\u0000`);
                    return (
                      <div className="mt-3 rounded-lg border border-amber-700/40 bg-amber-950/20 p-2.5">
                        <div className="flex gap-2">
                          <AlertTriangle size={12} className="text-amber-400 mt-0.5 flex-shrink-0" />
                          <p className="text-amber-200/90 text-[11px] leading-relaxed">
                            {parts[0]}
                            <strong>{label}</strong>
                            {parts[1] || ""}
                          </p>
                        </div>
                      </div>
                    );
                  })()}
                  <div className="mt-4 flex flex-col sm:flex-row gap-2">
                    <input
                      type="text"
                      value={rejectionInputs[req.id] || ""}
                      onChange={(e) => setRejectionInputs((prev) => ({ ...prev, [req.id]: e.target.value }))}
                      placeholder={t.crRejectionPlaceholder || "Rejection reason (optional)"}
                      className="flex-1 px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white text-xs focus:border-gray-600 focus:outline-none"
                      data-testid={`change-request-rejection-input-${req.id}`}
                    />
                    <button
                      onClick={() => handleReject(req)}
                      disabled={isBusy}
                      className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-200 text-xs font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
                      data-testid={`change-request-reject-${req.id}`}
                    >
                      <XCircle size={14} />
                      {t.crRejectBtn || "Reject"}
                    </button>
                    <button
                      onClick={() => handleApprove(req)}
                      disabled={isBusy}
                      className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
                      data-testid={`change-request-approve-${req.id}`}
                    >
                      {isBusy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                      {urgent ? (t.crApproveResendBtn || "Approve and resend email") : (t.crApproveBtn || "Approve")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        };

        const preSigCountKey = preSig.length === 1 ? "crPreSigCountOne" : "crPreSigCountMany";
        const postSigCountKey = postSig.length === 1 ? "crPostSigCountOne" : "crPostSigCountMany";

        return (
          <div className="space-y-6" data-testid="change-requests-groups">
            {preSig.length > 0 && (
              <section data-testid="change-requests-presig-section">
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-900/40 border border-red-800 text-red-200 text-[11px] font-bold uppercase tracking-wider">
                    <PauseCircle size={12} />
                    {t.crPreSigBadge || "Pre-signature · Urgent"}
                  </span>
                  <span className="text-gray-500 text-xs">
                    {fmtI18n(t[preSigCountKey], { n: preSig.length }) || `${preSig.length} ${preSig.length === 1 ? "request" : "requests"} — customers waiting to resume`}
                  </span>
                </div>

                {/* Zorzutti CR 03/08/2026 — SLA aggregate bar. Kept
                    compact + right-aligned counters so the dealer's eye
                    lands on the ONE number that means "act now": the
                    red breach count. */}
                <div
                  className="mb-4 flex items-center gap-2 flex-wrap"
                  data-testid="change-requests-sla-summary"
                  data-sla-breach={slaCounts.breach}
                  data-sla-warn={slaCounts.warn}
                  data-sla-ok={slaCounts.ok}
                >
                  <span className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mr-1">
                    {t.crSlaLabel || "SLA 24h >"}
                  </span>
                  {slaCounts.breach > 0 && (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-600/20 border border-red-500 text-red-100 text-[11px] font-bold animate-pulse"
                      data-testid="sla-count-breach"
                    >
                      <AlertTriangle size={11} />
                      {fmtI18n(t.crSlaBreachAggregate, { n: slaCounts.breach }) || `${slaCounts.breach} SLA breached`}
                    </span>
                  )}
                  {slaCounts.warn > 0 && (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-600/20 border border-amber-500 text-amber-100 text-[11px] font-bold"
                      data-testid="sla-count-warn"
                    >
                      <TimerReset size={11} />
                      {fmtI18n(t.crSlaWarnAggregate, { n: slaCounts.warn, h: SLA_WARN_H }) || `${slaCounts.warn} in warning (>${SLA_WARN_H}h)`}
                    </span>
                  )}
                  {slaCounts.ok > 0 && (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-900/30 border border-emerald-700/60 text-emerald-200 text-[11px] font-semibold"
                      data-testid="sla-count-ok"
                    >
                      <Clock size={11} />
                      {fmtI18n(t.crSlaOkAggregate, { n: slaCounts.ok }) || `${slaCounts.ok} on time`}
                    </span>
                  )}
                </div>

                <div className="space-y-3">
                  {preSig.map((req) => renderCard(req, true))}
                </div>
              </section>
            )}
            {postSig.length > 0 && (
              <section data-testid="change-requests-postsig-section">
                <div className="flex items-center gap-2 mb-3">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-800 border border-gray-700 text-gray-300 text-[11px] font-bold uppercase tracking-wider">
                    {t.crPostSigBadge || "Post-signature"}
                  </span>
                  <span className="text-gray-500 text-xs">
                    {fmtI18n(t[postSigCountKey], { n: postSig.length }) || `${postSig.length} ${postSig.length === 1 ? "request" : "requests"} — contract already signed`}
                  </span>
                </div>
                <div className="space-y-3">
                  {postSig.map((req) => renderCard(req, false))}
                </div>
              </section>
            )}
          </div>
        );
      })()}
    </div>
  );
}
